/**
 * Button.
 *
 * Instrument doctrine, motif 2, the ink block: exactly one maximum contrast
 * filled control per screen, and it is always the next thing to do. Everything
 * else on the screen is an outline or a bare label. The founder's glow
 * standard is translated rather than rejected: an enabled control is
 * unmistakable and a disabled control is visibly inert, but the mechanism is
 * fill weight, not light bleed, because a commercial console does not halo its
 * buttons.
 *
 * Danger is an outline, never a fill. That is a deliberate deviation from
 * screen spec 4.5, which asks for a filled red primary: a second filled
 * control on the page would break the one ink block rule and would put a
 * saturated fill on a screen whose whole argument is that saturation means
 * status. Red hairline plus red label plus the object's real name in the
 * label carries the same weight without spending the rule.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import '../../styles/components/button.css';

export type ButtonVariant = 'solid' | 'outline' | 'ghost' | 'danger-outline';

/** 28, 32 and 36 pixels of minimum height. Never a fixed height: Thai runs
 *  1.4x and a fixed height clips it. */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Passing the prop at all, in either position, reserves the glyph slot for
   * the life of the button, so a button that starts working does not grow and
   * shove the rest of the row sideways.
   */
  loading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'outline',
  size = 'md',
  loading,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const reservesArc = loading !== undefined;

  return (
    <button
      type={type}
      className={className ? `btn ${className}` : 'btn'}
      data-variant={variant}
      data-size={size}
      disabled={disabled === true || loading === true}
      aria-busy={loading === true ? true : undefined}
      {...rest}
    >
      {reservesArc ? (
        <span className="btn__arc" data-on={loading === true ? 'yes' : 'no'} aria-hidden="true" />
      ) : null}
      <span className="btn__label">{children}</span>
    </button>
  );
}
